import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  getAllAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  toggleAnnouncementActive,
} from "@/services/announcementService";
import type { Announcement, FirestoreDate } from "@/types/announcement";
import {
  AnnouncementType,
  AnnouncementAction,
  AnnouncementTrigger,
} from "@/types/announcement";
import { queryKeys } from "@/lib/queryKeys";
import { Plus, Pencil, Trash2, Megaphone } from "lucide-react";
import { toast } from "sonner";

function formatFirestoreDate(value: FirestoreDate | null | undefined): string {
  if (value == null) return "\u2014";
  const d =
    value instanceof Date
      ? value
      : "toDate" in value && typeof (value as { toDate: () => Date }).toDate === "function"
        ? (value as { toDate: () => Date }).toDate()
        : "seconds" in value
          ? new Date((value as { seconds: number }).seconds * 1000)
          : null;
  return d
    ? d.toLocaleDateString("es-DO", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "\u2014";
}

function firestoreDateToInputValue(value: FirestoreDate | null | undefined): string {
  if (value == null) return "";
  const d =
    value instanceof Date
      ? value
      : "toDate" in value && typeof (value as { toDate: () => Date }).toDate === "function"
        ? (value as { toDate: () => Date }).toDate()
        : "seconds" in value
          ? new Date((value as { seconds: number }).seconds * 1000)
          : null;
  if (!d) return "";
  return d.toISOString().slice(0, 10);
}

const TYPE_LABELS: Record<string, string> = {
  [AnnouncementType.POPUP]: "Popup",
  [AnnouncementType.BANNER]: "Banner",
  [AnnouncementType.TOAST]: "Toast",
};

const TYPE_COLORS: Record<string, string> = {
  [AnnouncementType.POPUP]: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200",
  [AnnouncementType.BANNER]: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200",
  [AnnouncementType.TOAST]: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200",
};

const ACTION_LABELS: Record<string, string> = {
  [AnnouncementAction.NONE]: "Ninguna",
  [AnnouncementAction.NAVIGATE_TO_PRODUCT]: "Navegar a producto",
  [AnnouncementAction.NAVIGATE_TO_CATEGORY]: "Navegar a categoría",
  [AnnouncementAction.NAVIGATE_TO_OFFER]: "Navegar a oferta",
  [AnnouncementAction.NAVIGATE_TO_URL]: "Navegar a URL",
  [AnnouncementAction.OPEN_CART]: "Abrir carrito",
};

const TRIGGER_LABELS: Record<string, string> = {
  [AnnouncementTrigger.ON_APP_OPEN]: "Al abrir app",
  [AnnouncementTrigger.ON_PAGE_VISIT]: "Al visitar página",
  [AnnouncementTrigger.ON_TIME_DELAY]: "Con retraso",
  [AnnouncementTrigger.MANUAL]: "Manual",
};

interface AnnouncementFormValues {
  title: string;
  message: string;
  type: AnnouncementType;
  action: AnnouncementAction;
  actionData: string;
  buttonText: string;
  trigger: AnnouncementTrigger;
  imageUrl: string;
  isActive: boolean;
  startDate: string;
  endDate: string;
}

type TabFilter = "all" | "active" | "inactive";

export function Notifications() {
  const queryClient = useQueryClient();
  const { data: announcements = [], isLoading } = useQuery({
    queryKey: queryKeys.announcements,
    queryFn: getAllAnnouncements as () => Promise<Announcement[]>,
  });

  const [activeTab, setActiveTab] = useState<TabFilter>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingAnnouncement, setDeletingAnnouncement] = useState<Announcement | null>(null);
  const [saving, setSaving] = useState(false);

  const form = useForm<AnnouncementFormValues>({
    defaultValues: {
      title: "",
      message: "",
      type: AnnouncementType.POPUP,
      action: AnnouncementAction.NONE,
      actionData: "",
      buttonText: "",
      trigger: AnnouncementTrigger.ON_APP_OPEN,
      imageUrl: "",
      isActive: true,
      startDate: "",
      endDate: "",
    },
  });

  const watchAction = form.watch("action");

  const filtered = announcements.filter((a) => {
    if (activeTab === "active") return a.isActive;
    if (activeTab === "inactive") return !a.isActive;
    return true;
  });

  function openCreateDialog() {
    setEditingAnnouncement(null);
    form.reset({
      title: "",
      message: "",
      type: AnnouncementType.POPUP,
      action: AnnouncementAction.NONE,
      actionData: "",
      buttonText: "",
      trigger: AnnouncementTrigger.ON_APP_OPEN,
      imageUrl: "",
      isActive: true,
      startDate: "",
      endDate: "",
    });
    setDialogOpen(true);
  }

  function openEditDialog(a: Announcement) {
    setEditingAnnouncement(a);
    form.reset({
      title: a.title,
      message: a.message,
      type: a.type,
      action: a.action,
      actionData: a.actionData ?? "",
      buttonText: a.buttonText ?? "",
      trigger: a.trigger,
      imageUrl: a.imageUrl ?? "",
      isActive: a.isActive,
      startDate: firestoreDateToInputValue(a.startDate),
      endDate: firestoreDateToInputValue(a.endDate),
    });
    setDialogOpen(true);
  }

  async function onSubmit(values: AnnouncementFormValues) {
    setSaving(true);
    try {
      const data: Record<string, unknown> = {
        title: values.title,
        message: values.message,
        type: values.type,
        action: values.action,
        trigger: values.trigger,
        isActive: values.isActive,
      };
      if (values.actionData) data.actionData = values.actionData;
      if (values.buttonText) data.buttonText = values.buttonText;
      if (values.imageUrl) data.imageUrl = values.imageUrl;
      if (values.startDate) data.startDate = new Date(values.startDate);
      if (values.endDate) data.endDate = new Date(values.endDate);

      if (editingAnnouncement) {
        await updateAnnouncement(editingAnnouncement.id, data);
        toast.success("Anuncio actualizado");
      } else {
        await createAnnouncement(data);
        toast.success("Anuncio creado");
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.announcements });
      setDialogOpen(false);
    } catch {
      toast.error("Error al guardar el anuncio");
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(a: Announcement) {
    try {
      await toggleAnnouncementActive(a.id, !a.isActive);
      queryClient.invalidateQueries({ queryKey: queryKeys.announcements });
      toast.success(a.isActive ? "Anuncio desactivado" : "Anuncio activado");
    } catch {
      toast.error("Error al cambiar estado");
    }
  }

  async function handleDelete() {
    if (!deletingAnnouncement) return;
    try {
      await deleteAnnouncement(deletingAnnouncement.id);
      queryClient.invalidateQueries({ queryKey: queryKeys.announcements });
      toast.success("Anuncio eliminado");
      setDeleteDialogOpen(false);
      setDeletingAnnouncement(null);
    } catch {
      toast.error("Error al eliminar el anuncio");
    }
  }

  function confirmDelete(a: Announcement) {
    setDeletingAnnouncement(a);
    setDeleteDialogOpen(true);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Anuncios</h1>
          <p className="text-muted-foreground">
            Gestión de anuncios y notificaciones in-app
          </p>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="mr-2 h-4 w-4" />
          Nuevo Anuncio
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabFilter)}>
        <TabsList>
          <TabsTrigger value="all">Todos</TabsTrigger>
          <TabsTrigger value="active">Activos</TabsTrigger>
          <TabsTrigger value="inactive">Inactivos</TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <p className="text-muted-foreground">Cargando anuncios...</p>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-2 py-12 text-center text-muted-foreground">
            <Megaphone className="h-10 w-10 opacity-50" />
            <p>
              {announcements.length === 0
                ? "No hay anuncios creados."
                : "No hay anuncios en esta categoría."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filtered.map((a) => (
            <Card key={a.id}>
              <CardHeader className="pb-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-base">{a.title}</CardTitle>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${TYPE_COLORS[a.type] ?? ""}`}
                    >
                      {TYPE_LABELS[a.type] ?? a.type}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {TRIGGER_LABELS[a.trigger] ?? a.trigger}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {a.isActive ? "Activo" : "Inactivo"}
                      </span>
                      <Switch
                        checked={a.isActive}
                        onCheckedChange={() => handleToggleActive(a)}
                      />
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => openEditDialog(a)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => confirmDelete(a)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="line-clamp-2 text-sm text-muted-foreground">{a.message}</p>
                {(a.startDate || a.endDate) && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {formatFirestoreDate(a.startDate)} - {formatFirestoreDate(a.endDate)}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingAnnouncement ? "Editar Anuncio" : "Nuevo Anuncio"}
            </DialogTitle>
            <DialogDescription>
              {editingAnnouncement
                ? "Modifica los campos del anuncio."
                : "Completa los campos para crear un nuevo anuncio."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Título *</Label>
              <Input
                id="title"
                {...form.register("title", { required: true })}
                placeholder="Título del anuncio"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Mensaje *</Label>
              <Textarea
                id="message"
                {...form.register("message", { required: true })}
                placeholder="Mensaje del anuncio"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Controller
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={AnnouncementType.POPUP}>Popup</SelectItem>
                        <SelectItem value={AnnouncementType.BANNER}>Banner</SelectItem>
                        <SelectItem value={AnnouncementType.TOAST}>Toast</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label>Disparador</Label>
                <Controller
                  control={form.control}
                  name="trigger"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={AnnouncementTrigger.ON_APP_OPEN}>
                          Al abrir app
                        </SelectItem>
                        <SelectItem value={AnnouncementTrigger.ON_PAGE_VISIT}>
                          Al visitar página
                        </SelectItem>
                        <SelectItem value={AnnouncementTrigger.ON_TIME_DELAY}>
                          Con retraso
                        </SelectItem>
                        <SelectItem value={AnnouncementTrigger.MANUAL}>Manual</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Acción</Label>
              <Controller
                control={form.control}
                name="action"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ACTION_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {watchAction !== AnnouncementAction.NONE && (
              <div className="space-y-2">
                <Label htmlFor="actionData">Datos de acción</Label>
                <Input
                  id="actionData"
                  {...form.register("actionData")}
                  placeholder="ID del producto, URL, etc."
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="buttonText">Texto del botón</Label>
              <Input
                id="buttonText"
                {...form.register("buttonText")}
                placeholder="Ej: Ver más"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="imageUrl">URL de imagen</Label>
              <Input
                id="imageUrl"
                {...form.register("imageUrl")}
                placeholder="https://..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">Fecha inicio</Label>
                <Input id="startDate" type="date" {...form.register("startDate")} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">Fecha fin</Label>
                <Input id="endDate" type="date" {...form.register("endDate")} />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Controller
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                )}
              />
              <Label>Activo</Label>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Guardando..." : editingAnnouncement ? "Actualizar" : "Crear"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar Anuncio</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas eliminar el anuncio &quot;{deletingAnnouncement?.title}&quot;?
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
